/**
 * File: AuthPrompt.js
 * Origin: isotect
 * Description: AuthPrompt component for authentication forms
 *
 * Version: v2.0.0
 * Created: 2025-07-26
 * Last Updated: 2026-04-16
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { Link } from "react-router-dom";

/**
 * AuthPrompt component displays a short message with an actionable element used in auth workflows.
 *
 * @component
 *
 * @param {Object} props - Component properties
 * @param {string} props.message - Message displayed before the action element
 * @param {string} props.text - Text displayed inside the link or button
 * @param {string} [props.linkHref] - Target route for navigation (renders a Link if provided)
 * @param {Function} [props.onClick] - Click handler (renders a button if linkHref is not provided)
 *
 * @returns {JSX.Element} A styled prompt with a message and either a link or button action
 *
 * @example
 * // Navigation usage
 * <AuthPrompt
 *   message="Don't have an account?"
 *   text="Sign up"
 *   linkHref="/signup"
 * />
 *
 * @example
 * // Button usage
 * <AuthPrompt
 *   message="Want to retry?"
 *   text="Try again"
 *   onClick={handleRetry}
 * />
 */
function AuthPrompt({ message, text, linkHref, onClick }) {
    return (
        <>
            <div className="auth-prompt">
                <p>{message}</p>
                {linkHref ? (
                    <Link className="auth-prompt__link" to={linkHref}>
                        {text}
                    </Link>
                ) : (
                    <button
                        className="auth-prompt__button"
                        type="button"
                        onClick={onClick}
                    >
                        {text}
                    </button>
                )}
            </div>
        </>
    );
}

export default AuthPrompt;
